// Player map — where everyone is, and the full interactive Map. Positions
// come from the snapshot stream the client already receives (every entity,
// 20 Hz, no interest culling), so this needs NO protocol change. ONE painter
// family serves three surfaces:
//   · corner minimap (flat + mobile DOM canvas, centred on you) — _paint
//     (round, `streets`): a STREET map (lakes, rivers, roads + rails of the
//     way graph, trails, lifts — maplayers.js painters, no imagery), pin,
//     players, you. Painted north-up every MINI_MS; HEADING-UP by a CSS
//     rotation of the canvas each frame (_miniTurn: up = where you move,
//     the view's heading when still), #pmap-n rides the rim to true north
//   · full map = the Map view (desktop: RMB / N / click the minimap / its
//     "2D" button (main.js), north-up;
//     phone: tip the phone flat, or the no-gyro map button; always opened
//     NORTH-LOCKED: the view spins to north-up, the phone's heading never
//     turns it, a two-finger twist does; 🧭 releases it to follow the phone) —
//     _paintFull: a STATIC layer (the class map + the zoom-sensitive layers
//     and labels of maplayers.js) cached on an offscreen canvas per view,
//     under a DYNAMIC overlay (players, you + the view wedge, the waypoint,
//     the inspect pin / selection, north arrow, scale bar, crosshair).
//     Pan (drag), zoom (wheel / pinch / + −), 🧭 north-up ⇄ heading-up (phone:
//     north lock on ⇄ follows the phone; twist = rotate, locked only), ◎
//     back to you; a tap on a player dot = FOLLOW, any other tap / click =
//     onTapWorld (main.js → Inspect.openAt: the same inspect card as the 3D
//     view); desktop hover = a light tooltip; a readout of LV95 E/N +
//     elevation (the cursor on desktop, the centre crosshair on the phone).
//     The phone's GPS fix (gpsFix, main.js): a blue dot + accuracy ring.
//     Quit (onQuit, main.js askQuit): × before entry, ⏻ in the game.
//     "?" (onHelp): the Map's briefing (briefing.js), before entry and in
//     the game; onOpen tells main.js each open (its first in-game briefing).
//     Before entry the top bar says it is loading + the device's few words
//     (HINT) until the class map is in (and at least HINT_MS), then nothing.
//   · VR wrist map (canvas texture on the LEFT controller, heading-up; shows
//     when the back of the wrist is turned to the eyes) — _paint (round)
// Desktop + phone: always enabled. With combat on (`combat` flag) the map
// shows yourself and the terrain only — no other players, no follow, no
// beacon waypoint (it would be a radar; beacons.js hides with the same
// flag). VR: combat off only (main.js passes enabled=false).
// Backdrop = the CLASS MAP painted on the device (mapclass.js / maptiles.js
// MapClass; the bake ships no Map imagery): the land-cover underlay (genome
// class colours x the 16 m hillshade; 16 m/px the moment coarse.png +
// genome.json land, from the constructor; refined ONCE to the device's
// tunables map.underlay_m only while the full Map is open), and the zoomed
// Map's 512 m class tiles (+ the residual) once mpp x TEXEL_SLACK is under
// that texel.
// Trail layer (TRAILS-C) between the backdrop and the lift lines: the trail
// index (WaysNet.trails, client/src/trailmarks.js) in the swisstopo
// Wanderkarte convention — Wanderweg yellow solid, Bergwanderweg red dashed,
// Alpinwanderweg blue dashed, via ferrata blue dotted, ungraded paths thin
// grey (maplayers.js MARK_LINE; the mini / wrist map paints exactly these).
// The full Map's TRAIL LADDER (MAP-D, maplayers.js LAYERS): official network
// + route bands ≤ 16 m/px, every path ≤ 8, route shields + MTB colours ≤ 4,
// grade chips + names + saddles / summit crosses ≤ 2, width / surface /
// visibility styling + incline chevrons + amenities ≤ 1, small amenities +
// TLM structures ≤ 0.5 — trail strokes batched by style key (trailBuckets,
// built once per data generation). The hiking trails + their gradings are
// ALWAYS on (owner: no switch — every map, the mini / wrist trail layer, the
// 3D trail lines). The CHIPS strip (#pmap-chips): MTB, Amenities; persisted
// in localStorage (a stale `hiking` key is ignored). A route card highlights its route (`highlightRoute`: the
// overlay draws it, the static layer dims the other bands) and fits it.
import * as THREE from 'three';
import { t } from './i18n.js';
import './i18n/player.js';
import { gradeShort, mtbClass } from './trailmarks.js';
import { TileCache, MapClass, drawTiles, closeBitmap } from './maptiles.js';
import { CAP, MIN_MPP, underlayM, tilesOn, tilesWanted, viewRect } from './mapclass.js';
import { deviceClass, classifyDevice } from './device.js';
import {
  layerOn, applyWorld, toWorld, niceScale, registryIndex, collectLabels, placeLabels, drawLabels, hoverPick,
  paintLakes, paintRivers, paintRoads, paintFootprints, paintBuildingDots, paintObjects, paintGuideposts,
  paintTrees, paintShrubs, MARK_LINE, trailBucketsSteps, paintTrailBuckets, mtbBuckets, paintMtb, paintChevrons,
  paintRouteBands, paintStructs, arcStamp, ROUTE_STYLE, routeFamily, amenityNear, amenityLabel, AMENITY,
} from './maplayers.js';

const STEP = 256;               // trail entries per linesSteps step
const drain = (it) => { for (;;) { const r = it.next(); if (r.done) return r.value; } };

// same golden-angle hue as the dashboard's player map (public/server.js)
export const playerHue = (id) => (id * 137.5) % 360;
const playerCss = (id) => `hsl(${playerHue(id)} 75% 60%)`;
// the slope-ahead readout (main.js _slopeAhead, whole degrees): ↗ +7° · ↘ −3° · → 0°
export const slopeLabel = (d) => (d > 0 ? `↗ +${d}°` : d < 0 ? `↘ −${-d}°` : '→ 0°');

const MINI_MS = 160, FULL_MS = 50, WRIST_MS = 160;   // repaint periods
const WRIST_PX = 512, WRIST_M = 0.16;                // texture px, plane size m
const TAP_PX = 22;                                   // full-map player-dot tap radius (CSS px)
// a press is a tap below this much pointer travel (CSS px). The Quest's
// controller ray (the VR entry's flat page) jitters and the trigger pull
// flinches the hand by 0.5–2° ≈ 10–40 px on the browser panel: its press is a
// tap within VR_TAP_PX of the DOWN point (displacement, not path), it pans
// only once past it, and the tap lands where the ray was when the pull began
const MOUSE_TAP_PX = 8, VR_TAP_PX = 28;
const PICK_PX = { mouse: 14, touch: 22 };            // map-card pick radius (CSS px)
const SPOT_PX = 9, SPOT_HIT_PX = 24;                 // a beam spot's radius / tap radius (CSS px: a 48 px target)
const STATIC_MIN_MS = 66;     // while panning / zooming / tracking, the static layer re-renders at most this often
const STATIC_MIN_MS_MOBILE = 100;
const INPUT_HOT_MS = 180;     // ... "while" = an input within this long
const TILE_MIN_MS = 150;      // class tiles landing in view: one static re-render per this long at most (a burst coalesces)
const HOVER_MS = 100;         // tooltip + readout refresh (≤ 10 Hz)
const MOBILE_CAP = 160;       // phone LRU (decoded 512² tiles are 1 MB each)
const MOVE_MIN = 0.8;         // m/s: faster = the minimap's up is where you move, else where you look
const STREET_BG = '#e9e4d8';  // the minimap's street-map ground
const STREET_NONE = 'rgba(70,70,70,.55)';   // ungraded trails on it (MARK_LINE's grey is for imagery)
const TWIST_DEAD = 9 * Math.PI / 180;   // the phone Map: a two-finger twist engages past this (a plain pinch never wobbles)
const SPIN_MS = 380;          // the phone Map spins to north-up (north lock) over this
const HINT_MS = 4000;         // the Map's first-load note shows at least this long
// the Map's first-load words, by entry mode (the controls the Map has)
const HINT = { flat: () => t('player.map.hintFlat'), mobile: () => t('player.map.hintMobile'), vr: () => t('player.map.hintVr') };
// the in-game Map's words per mode (the DOM Map is flat / phone; VR's is the
// in-headset panel, vrui.js, which uses the 'vr' verb)
const NOTE_VERB = { flat: () => t('player.map.verbFlat'), mobile: () => t('player.map.verbMobile'), vr: () => t('player.map.verbVr') };
const CLOSE_TITLE = { flat: () => t('player.map.closeFlat'), mobile: () => t('player.map.closeMobile'), vr: () => t('player.map.closeVr') };
const _v = new THREE.Vector3(), _n = new THREE.Vector3(), _f = new THREE.Vector3();
// trail strokes by mark, drawn in this order (graded over ungraded); width
// and dash in CSS px (scaled by the view's px and mpp in the world layer) —
// the mini / wrist map's (maplayers.js MARK_LINE, colours = MARK_COLOUR)
const TRAIL_STYLE = ['none', 'yellow', 'wrw', 'wbw', 'ferrata'].map((m) => [m, ...MARK_LINE[m]]);
const CHIPS_KEY = 'vrm.map.chips';   // localStorage: the Map's chips {mtb, amen}
// tt.incline → +1 uphill along the run / −1 downhill / 0
// the beam spot nearest canvas px (sx, sy) within r px, in a view {cx, cz,
// mpp, rot} on a W × H canvas — null = none (PlayerMap._tap, vrui.js)
export function spotAt(spots, view, W, H, sx, sy, r) {
  const c = Math.cos(view.rot || 0), s = Math.sin(view.rot || 0);
  let best = null, bd = r;
  for (const p of spots || []) {
    const ex = (p.x - view.cx) / view.mpp, ez = (p.z - view.cz) / view.mpp;
    const d = Math.hypot(W / 2 + ex * c + ez * s - sx, H / 2 - ex * s + ez * c - sy);
    if (d <= bd) { bd = d; best = p; }
  }
  return best;
}

function inclineSign(v) {
  if (v === undefined || v === null) return 0;
  const s = String(v).trim();
  if (s === 'up') return 1;
  if (s === 'down') return -1;
  const n = parseFloat(s);
  return Number.isFinite(n) && n !== 0 ? Math.sign(n) : 0;
}

// 850 m / 1.2 km
export function fmtDist(m) {
  return m < 1000 ? `${Math.round(m / 10) * 10} m` : `${(m / 1000).toFixed(1)} km`;
}
const fmtInt = (n) => String(Math.round(n)).replace(/\B(?=(\d{3})+(?!\d))/g, ' ');

export class PlayerMap {
  // rides: { lines } (funicular + aerial paths); getZones(): StationZones|null;
  // meta: the bake meta (the class Map's inputs: surface coarse / tiles /
  // genome / s2col / residual, tex.light, lighting); heights: the
  // MapHeights (mapheights.js: the GROUND-M 16 m posts — the hillshade;
  // the elevation readout reads its heightAt via src.elevAt); src: main.js getters onto the
  // resident data (see main.js mapSrc) — the full map's layers
  constructor({ baseUrl, extentX, extentZ, rides, getZones, trails = null, meta = null, heights = null, src = null }) {
    this.ex = extentX; this.ez = extentZ;
    this.rides = rides; this.getZones = getZones;
    this.src = src || {};
    this.trails = null;         // trail index entries by mark (setTrails; the mini / wrist map)
    this._lines = [];           // the full Map's decorated trail entries (+ TLM-only, R5)
    this._bk = null;            // trail stroke buckets {network, coarse, fine, mtb, inc} per data generation
    this._trailGen = 0;
    this.extras = { routes: null, structs: null, amenities: null, extra: null };   // setTrailExtras
    this._extraGen = 0;
    this.hl = null;             // {ri, si} the route card's highlighted route (stage si, -1 = whole)
    // the Map's chips (hiking + gradings are always on, not a chip)
    this.chips = { mtb: false, amen: true };
    try {
      const c = JSON.parse((typeof localStorage !== 'undefined' && localStorage.getItem(CHIPS_KEY)) || 'null');
      if (c && typeof c === 'object') for (const n of Object.keys(this.chips)) if (typeof c[n] === 'boolean') this.chips[n] = c[n];
    } catch { /* private mode / bad JSON: the defaults */ }
    this.onChip = null;         // main.js: (name, on) after a chip tap (telemetry)
    this.slopeDeg = null;       // setSlope: the way ahead's incline (phone chip under the mini map, VR wrist)
    if (trails) this.setTrails(trails);
    this._img = null; this._imgOk = false;
    this._imgGen = 0;           // +1 per underlay swap (the static layer's key)
    this._imgM = [extentX, extentZ];   // the underlay's world size (m)
    this.cfg = {};
    this.followId = null;       // player the full map was tapped on (beam emphasis)
    this.list = [];             // [{id,name,x,z,st}] humans, alive, not self
    this.enabled = false;
    this.mode = null;
    // BEFORE ENTRY (MAPFIRST, APP_PLAYER §7): the full Map is the page —
    // openPreEntry(entryMode) opens it without `enabled` / a game mode; no
    // self (no arrow, heading-up, ◎, mini map or wrist) until the player is
    // placed. endPreEntry() at the beam.
    this.preEntry = false;
    this._self = null;
    this.onHelp = null;         // main.js: the Map's "?" (its briefing, briefing.js)
    this.onOpen = null;         // main.js: the full Map opened (the first in-game Map's briefing)
    this.onQuit = null;         // main.js: (where 'map' | 'game') the × before entry / ⏻ — confirm, leave
    this.gpsFix = null;         // {x, z, acc} the phone's GPS fix in game metres (main.js), full Map only
    this._entry = null;         // the launch tap's entry mode (before entry: HINT)
    this._mapReady = false;     // the first load is in (the note goes); once a session
    this._hintAt = 0;
    this._mv = null;            // {x, z, t, vx, vz} your motion (the minimap's up)
    this._miniRot = 0; this._miniCss = NaN;
    this._miniAt = 0; this._fullAt = 0; this._wristAt = 0;
    this._full = null;          // {cx, cz, mpp, rot, headingUp, track} full-map view, null = closed
    this._lastMpp = null;       // the full map reopens at the zoom it was left at
    this.combat = false;        // self + terrain only (update)
    this.onClose = null;        // main.js: the map closed (re-lock the pointer, close a map card)
    this.onTapWorld = null;     // main.js: a tap / click off the player dots → (x, z, info)
    this.onVrMap = null;        // main.js: (x, z) → the in-headset Map panel opened there (vrui.js)
    // the world's red beam spots (meta.beam_spots, setSpots): one tap =
    // onSpot(spot, info) → main.js beamSpot, above every other tap target
    this.spots = [];
    this.onSpot = null;
    this._flash = null;         // {text, until}: a beam spot's outcome in the top bar (flash)
    this.mark = null;           // {x, z, label} the inspect card's map pin (focus / Look)
    this.sel = null;            // {x, z} the open map card's target (a ring, full map)
    this.wedge = null;          // {half (rad), len (m), range {x, z} | null}: your view on the map
    // the class Map's producer (a browser with a bake meta; the gates' Node: none)
    this._mc = typeof Image !== 'undefined' && meta ? new MapClass({ baseUrl, meta, heights,
      onImg: (img, wM, hM) => this._imgIn(img, wM, hM) }) : null;
    this._tiles = this._mc
      ? new TileCache({ load: (t) => this._mc.tile(t), cap: CAP, onLoad: (t) => this._tileIn(t) }) : null;   // the 50 ms paint picks it up
    this._tileGen = 0;          // +1 per class tile that landed IN VIEW (the static layer's key)
    this._static = null;        // {cv, key, view, at} the cached static layer
    this._placed = [];          // labels placed on it (tap targets, static-canvas px)
    this._wcache = new Map();   // text width cache (label measure)
    this._dirty = false;        // input changed the view: repaint on the next tick
    this._covered = false;      // the beam-in scrim is over the Map (setCovered): no full repaint
    this._inputAt = -1e9;       // last pan / zoom input (the static layer's rate limit)
    this._hover = null;         // {sx, sy (canvas px), cx, cy (client px)} desktop cursor
    this._touchAt = -1e9;       // last touch pan (the phone's centre crosshair)
    this._roAt = 0;
    this._wrist = null;         // { mesh, canvas, tex, op }
    this._buildDom();
    // MAP FIRST: the 16 m underlay is painted from boot, so a launch tap
    // opens the Map on the class map, not the grid
    if (this._tiles) this._loadImg();
  }

  applyTunables(t) { this.cfg = (t && t.map) || {}; }

  // meta.beam_spots [{id, label, x, z}] (game metres; null = none): kept
  // inside the map
  setSpots(list) {
    this.spots = (Array.isArray(list) ? list : []).filter((p) => p && Number.isFinite(p.x) && Number.isFinite(p.z)
      && p.x >= 0 && p.z >= 0 && p.x <= this.ex && p.z <= this.ez)
      .map((p) => ({ id: String(p.id), label: String(p.label || p.id), x: p.x, z: p.z }));
    this._fullAt = 0;
  }

  // the spots are drawn and tappable: before entry always (the first place
  // is accepted in combat), in the game with combat off
  get spotsOn() { return this.spots.length > 0 && (this.preEntry || !this.combat); }

  // a line in the Map's top bar for ms (a beam spot's refusal)
  flash(text, ms = 5000) { this._flash = text ? { text, until: performance.now() + ms } : null; }

  // trails: WaysNet.trails ({mark, pts (xz Float32Array), bbox}); grouped by
  // mark once so a paint is one stroke per style
  // The full Map decorates each entry once: chipT (T3 / K4), mtb
  // (mtbClass), vis, inc (tt.incline ±1) + arc / len (withArc).
  setTrails(trails) { drain(this.linesSteps(trails))(); }

  // MAP-D artifacts (main.js, all optional): routes (trailmarks.js
  // prepRoutes), extra (prepExtra: TLM-only segments, R5 — drawn + tappable,
  // never walkable), structs (pathinfo.structs), amenities (prepAmenities)
  setTrailExtras(x = {}) { drain(this.linesSteps(undefined, x))(); }

  // setTrails (trails) and / or setTrailExtras (x) IN STEPS (R8 M1: the Map
  // pack's pair was ONE 56-63 ms task) -> commit(), the one act that swaps
  // the new lines in (the Map paints the old ones until then; main.js drops
  // a commit the ways overtook). undefined = that half stays as it is; the
  // other half is read at the start. The trail stroke buckets per data
  // generation: network = the official network (pathinfo-matched + TLM-only)
  // at ≤ 16, coarse = every path ≤ 8, fine = width / surface / visibility ≤ 1.
  // The setters above = its drain.
  *linesSteps(trails, x) {
    let by = null, trailsIn = this._trailsIn, X = this.extras, k = 0;
    if (trails !== undefined) {
      by = {};
      for (const [m] of TRAIL_STYLE) by[m] = [];
      trailsIn = [];
      for (const t of trails || []) {
        if (++k % STEP === 0) yield;
        if (!t.pts || t.pts.length < 4) continue;
        (by[t.mark] || by.none).push(t);
        const tt = t.tt || {};
        t.chipT = gradeShort(t);
        t.mtb = mtbClass(tt.mtb);
        t.vis = tt.vis || null;
        t.inc = inclineSign(tt.incline);
        arcStamp(t);
        trailsIn.push(t);
      }
    }
    if (x !== undefined) {
      const { routes = null, extra = null, structs = null, amenities = null } = x;
      X = { routes, extra, structs, amenities };
      for (const e of extra || []) {
        if (++k % STEP === 0) yield;
        e.chipT = gradeShort(e); e.mtb = null; e.vis = null; e.inc = 0;
        arcStamp(e);
      }
    }
    const lines = (trailsIn || []).concat(X.extra || []);
    const bk = {
      network: yield* trailBucketsSteps(lines, false, (e) => !!(e.off || e.extra) && e.mark !== 'none'),
      coarse: yield* trailBucketsSteps(lines, false),
      fine: yield* trailBucketsSteps(lines, true),
      mtb: mtbBuckets(lines),
      inc: lines.filter((e) => e.inc),
    };
    return () => {
      if (by) {
        this.trails = by; this._trailsIn = trailsIn;
        this._trailGen++;
        this._miniAt = 0; this._fullAt = 0; this._wristAt = 0;
      }
      if (x !== undefined) {
        this.extras = X;
        this._extraGen++;
        this._fullAt = 0; this._dirty = true;
      }
      this._lines = lines; this._bk = bk;
    };
  }

  // a Map chip (mtb / amen) on / off, persisted
  setChip(name, on) {
    if (!(name in this.chips)) return;
    this.chips[name] = !!on;
    try { if (typeof localStorage !== 'undefined') localStorage.setItem(CHIPS_KEY, JSON.stringify(this.chips)); } catch { /* quota / private */ }
    this._syncChips();
    this._miniAt = 0; this._fullAt = 0; this._wristAt = 0; this._dirty = true;
  }

  _syncChips() {
    for (const b of this._chipBtns || []) {
      const on = !!this.chips[b.dataset.chip];
      b.classList.toggle('on', on);
      b.setAttribute('aria-pressed', on ? 'true' : 'false');
    }
  }

  // the slope of the way ahead (whole degrees, null = none): the phone's chip
  // under the mini map; the VR wrist paints it on its next repaint
  setSlope(deg) {
    this.slopeDeg = deg;
    const el = this._slopeEl;
    if (!el) return;
    const t = deg === null ? '' : slopeLabel(deg);
    if (el.textContent !== t) el.textContent = t;
    if (el.hidden !== (deg === null)) el.hidden = deg === null;
  }

  // the route card: highlight route ri (stage si, -1 = all) — the overlay
  // draws it, the static layer dims the other bands; null clears
  highlightRoute(ri, si = -1) {
    this.hl = Number.isInteger(ri) && ri >= 0 ? { ri, si: Number.isInteger(si) ? si : -1 } : null;
    this._fullAt = 0; this._dirty = true;
  }

  // fit the bbox [x0, z0, x1, z1] in the open full Map (a margin; never
  // closer than 1 m/px) → false when the Map is closed
  fitBox(b, top = null) {
    const v = this._full, cv = this._fullCv;
    if (!v || !cv || !b) return false;
    const W = cv.width, H = cv.height, d = W / Math.max(1, cv.clientWidth);
    // the phone's bottom sheet covers the map from client y `top` down
    const hv = top !== null ? Math.max(H * 0.3, (top - cv.getBoundingClientRect().top) * d) : H;
    const w = Math.max(1, b[2] - b[0]), h = Math.max(1, b[3] - b[1]);
    v.mpp = Math.min(Math.max(Math.max(w / (W * 0.84), h / (hv * 0.84)), 1 / d, MIN_MPP), this._fitMpp() * 1.5);
    v.rot = 0; v.anim = null; v.headingUp = false; v.track = false;
    v.cx = (b[0] + b[2]) / 2;
    v.cz = (b[1] + b[3]) / 2 + (H - hv) / 2 * v.mpp;
    this._lastMpp = v.mpp;
    this._syncNorthBtn();
    this._dirty = true;
    return true;
  }

  // pin (x, z) on every map surface (the compass strip's marker follows it)
  pin(x, z, label = '') {
    this.mark = { x, z, label: String(label || '') };
    this._miniAt = 0; this._fullAt = 0; this._wristAt = 0;
  }

  // the inspect card's 🗺: pin (x, z) on every map surface and open the full
  // map centred there at ~2 m/px (flat + phone); VR: the in-headset Map
  // panel there (onVrMap → vrui.js). → true when a map opened.
  focus(x, z, label = '') {
    this.pin(x, z, label);
    if (this.mode === 'vr' && !this.preEntry && this.onVrMap) return this.onVrMap(x, z) === true;
    if (!this._mapOk() || !this._fullWrap) return false;
    if (!this._full) this.openFull();
    if (!this._full) return false;
    this._full.cx = x; this._full.cz = z; this._full.track = false;
    this._full.mpp = Math.min(this._full.mpp, 2);
    this._dirty = true;
    return true;
  }

  // the map card's target ring (null clears); main.js from inspect
  select(p) { this.sel = p ? { x: p.x, z: p.z } : null; this._dirty = true; }

  // phone: the bottom sheet covers the map from client y `top` down — pan so
  // (x, z) shows above it
  reveal(x, z, top) {
    const v = this._full, cv = this._fullCv;
    if (!v || !cv) return;
    const d = cv.width / Math.max(1, cv.clientWidth), W = cv.width, H = cv.height;
    const c = Math.cos(v.rot || 0), s = Math.sin(v.rot || 0);
    const dx = (x - v.cx) / v.mpp, dz = (z - v.cz) / v.mpp;
    const sy = H / 2 - dx * s + dz * c;
    const lim = (top - cv.getBoundingClientRect().top) * d;
    if (sy < lim - 40 * d) return;
    const [ox, oz] = this._unrot(v, 0, sy - lim * 0.45);
    v.cx += ox; v.cz += oz; v.track = false;
    this._dirty = true;
  }

  // a class tile became drawable: the static layer re-renders only when it
  // is one the view wants (a tile of a view already left changes nothing)
  _tileIn(t) {
    if (this._tiles && this._tiles._wantKeys.has(t.key)) this._tileGen++;
  }

  // the class underlay: its inputs (coarse.png + genome.json + the top
  // terrain level, asset queue class `map`) and the 16 m first paint,
  // started by the constructor (boot); the device's refine waits for the
  // full Map (_refine)
  _loadImg() {
    if (this._img !== null || !this._mc) return;
    this._img = false;                   // loading
    this._mc.start();
  }

  // the underlay now {img, wM, hM} | null — the VR loading view's relief (xrrelief.js) draws it at once and never
  // keeps it (a finer one closes this one)
  underlay() { return this._imgOk ? { img: this._img, wM: this._imgM[0], hM: this._imgM[1] } : null; }

  // an underlay landed (16 m first, then the device's): it replaces the last
  _imgIn(img, wM, hM) {
    const old = this._imgOk ? this._img : null;
    this._img = img; this._imgOk = true; this._imgM = [wM, hM]; this._imgGen++; this._dirty = true;
    closeBitmap(old);
    this._refine();
  }

  // the device's underlay texel (m): tunables map.underlay_m by the device
  // class (decided at the renderer; before it, the user agent's)
  _underM() {
    const cls = deviceClass() || classifyDevice('', navigator.userAgent, (navigator.maxTouchPoints || 0) > 0);
    return underlayM(cls, this.cfg);
  }

  // the ONE refine to the device's texel, only while the full Map is open
  // (closeFull pauses it, the next open resumes it)
  _refine() {
    if (this._mc && this._full) this._mc.refine(this._underM(), () => !!this._full);
  }

  // ------------------------------------------------------------------ DOM
  _buildDom() {
    const $ = (id) => document.getElementById(id);
    this._miniWrap = $('pmap');
    this._mini = $('pmap-mini');
    this._miniNote = $('pmap-note');
    this._slopeEl = $('pmap-slope');
    this._fullWrap = $('pmap-full');
    this._fullCv = $('pmap-full-cv');
    this._fullNote = $('pmap-full-note');
    this._readout = $('pmap-readout');
    this._tip = $('pmap-tip');
    this._northBtn = $('pmap-full-north');
    this._miniN = $('pmap-n');
    if (!this._miniWrap) return;
    this._miniWrap.addEventListener('click', (e) => { e.stopPropagation(); this.openFull(); });
    // × before entry = quit (the landing page, confirmed); in the game it closes the Map
    this._xBtn = $('pmap-full-x');
    this._xBtn.addEventListener('click', () => {
      if (this.preEntry && this.onQuit) this.onQuit('map'); else this.closeFull('x');
    });
    this._quitBtn = $('pmap-full-quit');
    if (this._quitBtn) this._quitBtn.addEventListener('click', () => { if (this.onQuit) this.onQuit('game'); });
    this._meBtn = $('pmap-full-me');
    this._helpBtn = $('pmap-full-help');
    if (this._helpBtn) this._helpBtn.addEventListener('click', () => { if (this.onHelp) this.onHelp(); });
    // ◎: you (tracking) — before entry the GPS fix
    $('pmap-full-me').addEventListener('click', () => {
      const v = this._full;
      if (v && this._self) { v.cx = this._self.x; v.cz = this._self.z; v.track = true; this._dirty = true; }
      else if (v && this.gpsFix) { v.cx = this.gpsFix.x; v.cz = this.gpsFix.z; v.mpp = Math.min(v.mpp, 4); this._dirty = true; }
    });
    if (this._northBtn) this._northBtn.addEventListener('click', () => this.toggleHeadingUp());
    const zb = (id, k) => { const b = $(id); if (b) b.addEventListener('click', () => this._zoomAt(k)); };
    zb('pmap-full-in', 1 / 1.6);
    zb('pmap-full-out', 1.6);
    // the chips: MTB · Amenities
    this._chipBtns = [...document.querySelectorAll('#pmap-chips [data-chip]')];
    for (const b of this._chipBtns) {
      b.addEventListener('click', () => {
        const n = b.dataset.chip;
        this.setChip(n, !this.chips[n]);
        if (this.onChip) this.onChip(n, this.chips[n]);
      });
    }
    this._syncChips();
    this._wireFullInput();
  }

  // screen offset from the canvas centre (canvas px) → world offset (m) in a
  // view rotated by v.rot (the inverse of _paint's toS)
  _unrot(v, u, w) {
    const c = Math.cos(v.rot || 0), s = Math.sin(v.rot || 0);
    return [(u * c - w * s) * v.mpp, (u * s + w * c) * v.mpp];
  }

  _fitMpp() {
    const cv = this._fullCv;
    return Math.max(this.ex / Math.max(1, cv.width), this.ez / Math.max(1, cv.height)) * 1.04;
  }

  // turn the view by d rad about canvas point (sx, sy), the world point under
  // it staying put (rot + = the map turns counter-clockwise on the screen)
  _rotAt(d, sx, sy) {
    const v = this._full; if (!v) return;
    const W = this._fullCv.width, H = this._fullCv.height;
    const [ox, oz] = this._unrot(v, sx - W / 2, sy - H / 2);
    const wx = v.cx + ox, wz = v.cz + oz;
    v.rot = (v.rot || 0) + d;
    const [nx, nz] = this._unrot(v, sx - W / 2, sy - H / 2);
    v.cx = wx - nx; v.cz = wz - nz;
    v.track = false;
    this._inputAt = performance.now(); this._dirty = true;
  }

  // zoom by k about canvas point (sx, sy) (default: the centre), keeping the
  // world point under it fixed; mpp ∈ [MIN_MPP, fit · 1.5]
  _zoomAt(k, sx, sy) {
    const v = this._full; if (!v) return;
    const W = this._fullCv.width, H = this._fullCv.height;
    if (sx === undefined) { sx = W / 2; sy = H / 2; }
    const [ox, oz] = this._unrot(v, sx - W / 2, sy - H / 2);
    const wx = v.cx + ox, wz = v.cz + oz;
    v.mpp = Math.min(Math.max(v.mpp * k, MIN_MPP), this._fitMpp() * 1.5);
    const [nx, nz] = this._unrot(v, sx - W / 2, sy - H / 2);
    v.cx = wx - nx; v.cz = wz - nz;
    if (sx !== W / 2 || sy !== H / 2) v.track = false;
    this._lastMpp = v.mpp;
    this._inputAt = performance.now(); this._dirty = true;
  }

  // 🧭: north-up ⇄ heading-up (heading-up turns with you; the centre stays)
  // The phone's Map is NORTH-LOCKED by default (v.headingUp false: only a
  // two-finger twist turns it); the button releases it to follow the phone's
  // heading (v.headingUp true, as the desktop's heading-up) and locks it again
  // with a spin back to north-up. Desktop / VR: the plain toggle.
  toggleHeadingUp() {
    const v = this._full; if (!v) return;
    if (this.mode === 'mobile') {
      if (v.headingUp) { v.headingUp = false; this._spinNorth(v); }
      else if (this._self) { v.headingUp = true; v.anim = null; v.rot = this._self.heading; }
      else return;                          // no self (before entry): stays locked
    } else {
      v.headingUp = !v.headingUp;
      v.rot = v.headingUp && this._self ? this._self.heading : 0;
    }
    this._syncNorthBtn();
    this._dirty = true;
  }

  // an eased spin of the view to north-up (the shortest way round)
  _spinNorth(v) {
    const TAU = Math.PI * 2;
    const from = ((((v.rot || 0) + Math.PI) % TAU) + TAU) % TAU - Math.PI;
    if (Math.abs(from) < 1e-3) { v.rot = 0; v.anim = null; return; }
    v.anim = { from, t0: performance.now() };
    v.rot = from;
  }

  // the 🧭 button: desktop / VR = heading-up on/off; phone = north lock on
  // (class `on`) / released, its needle turned to where north is
  _syncNorthBtn() {
    const b = this._northBtn; if (!b) return;
    const v = this._full, ph = this.mode === 'mobile';
    if (ph) {
      const locked = !(v && v.headingUp);
      b.classList.toggle('on', locked);
      b.title = locked ? t('player.map.lockOn') : t('player.map.lockOff');
      if (!this._northNeedle) {
        b.innerHTML = '<svg viewBox="0 0 24 24" width="26" height="26" aria-hidden="true"><circle cx="12" cy="12" r="10" fill="none" stroke="currentColor" stroke-width="1.6"/>'
          + '<path d="M12 3.5l3.2 8.5h-6.4z" fill="#ef4444"/><path d="M12 20.5l3.2-8.5h-6.4z" fill="#e8ecf4"/></svg>';
        this._northNeedle = b.firstChild;
      }
      const tf = `rotate(${(-(v && v.rot || 0)).toFixed(3)}rad)`;
      if (this._northNeedle.style.transform !== tf) this._northNeedle.style.transform = tf;
      return;
    }
    if (this._northNeedle) { b.textContent = '🧭'; this._northNeedle = null; }
    const on = !!(v && v.headingUp);
    b.classList.toggle('on', on);
    b.title = on ? t('player.map.headingUp') : t('player.map.northUp');
  }

  // pan (drag), zoom (wheel / pinch), tap a dot = follow / unfollow, any
  // other tap = onTapWorld, hover = tooltip + readout — rotation-aware (the
  // phone: two fingers also twist + drag the view about their midpoint)
  _wireFullInput() {
    const cv = this._fullCv;
    const pts = new Map();
    let moved = 0, pinch0 = null, kind = 'mouse', d0x = 0, d0y = 0;
    let ang0 = 0, twistSum = 0, twistOn = false, mid0 = null;     // the phone's two-finger twist
    const dpr = () => cv.width / Math.max(1, cv.clientWidth);
    const ray = () => this.preEntry && this._entry === 'vr';      // the Quest browser's controller ray
    cv.addEventListener('wheel', (e) => {
      e.preventDefault();
      this._zoomAt(Math.exp(e.deltaY * 0.0015), e.offsetX * dpr(), e.offsetY * dpr());
    }, { passive: false });
    cv.addEventListener('pointerdown', (e) => {
      if (e.button !== undefined && e.button > 0) return;     // RMB = World ⇄ Map (main.js)
      cv.setPointerCapture(e.pointerId);
      pts.set(e.pointerId, { x: e.offsetX, y: e.offsetY });
      kind = e.pointerType === 'mouse' ? 'mouse' : 'touch';
      if (pts.size === 1) { moved = 0; d0x = e.offsetX; d0y = e.offsetY; }
      if (pts.size === 2) {
        const [a, b] = [...pts.values()];
        pinch0 = Math.hypot(a.x - b.x, a.y - b.y);
        ang0 = Math.atan2(b.y - a.y, b.x - a.x); twistSum = 0; twistOn = false;
        mid0 = [(a.x + b.x) / 2, (a.y + b.y) / 2];
      }
      if (this._tip) this._tip.hidden = true;
    });
    cv.addEventListener('pointermove', (e) => {
      const p = pts.get(e.pointerId); const v = this._full;
      if (!p) {
        if (e.pointerType === 'mouse') {
          this._hover = { sx: e.offsetX * dpr(), sy: e.offsetY * dpr(), cx: e.clientX, cy: e.clientY };
          const on = !!(this._full && this.spotsOn && spotAt(this.spots, this._full, cv.width, cv.height,
            this._hover.sx, this._hover.sy, SPOT_HIT_PX * dpr()));
          if ((cv.style.cursor === 'pointer') !== on) cv.style.cursor = on ? 'pointer' : '';
        }
        return;
      }
      if (!v) return;
      if (pts.size === 1 && ray() && moved < VR_TAP_PX) {
        // the ray's jitter inside the slop is no pan (p stays at the down
        // point, so a real drag starts from there without losing travel)
        if (Math.hypot(e.offsetX - d0x, e.offsetY - d0y) < VR_TAP_PX) return;
        moved = VR_TAP_PX;
      }
      const dx = e.offsetX - p.x, dy = e.offsetY - p.y;
      p.x = e.offsetX; p.y = e.offsetY;
      moved += Math.abs(dx) + Math.abs(dy);
      if (pts.size === 1) {
        const [ox, oz] = this._unrot(v, dx * dpr(), dy * dpr());
        v.cx -= ox; v.cz -= oz;
        if (moved > 8) v.track = false;     // a pan lets go of you
        this._inputAt = performance.now(); this._dirty = true;
        if (kind === 'touch') this._touchAt = this._inputAt;
      } else if (pts.size === 2 && pinch0) {
        const [a, b] = [...pts.values()];
        const d = Math.hypot(a.x - b.x, a.y - b.y);
        const m1 = [(a.x + b.x) / 2, (a.y + b.y) / 2];
        // the phone Map (locked): the twist of the two touches turns the view
        // about their midpoint, past a dead zone; released (follows the phone)
        // it is ignored, pinch + pan still work
        const ang = Math.atan2(b.y - a.y, b.x - a.x);
        let da = ang - ang0;
        da -= Math.round(da / (2 * Math.PI)) * 2 * Math.PI;
        ang0 = ang;
        if (kind === 'touch' && this.mode === 'mobile' && !v.headingUp) {
          if (!twistOn) {
            twistSum += da;
            if (Math.abs(twistSum) > TWIST_DEAD) { twistOn = true; v.anim = null; }
          } else {
            this._rotAt(-da, m1[0] * dpr(), m1[1] * dpr());
          }
        }
        if (mid0 && kind === 'touch') {        // the midpoint drags the map too
          const [ox, oz] = this._unrot(v, (m1[0] - mid0[0]) * dpr(), (m1[1] - mid0[1]) * dpr());
          v.cx -= ox; v.cz -= oz; v.track = false;
        }
        mid0 = m1;
        if (d > 0) this._zoomAt(pinch0 / d, m1[0] * dpr(), m1[1] * dpr());
        pinch0 = d;
        this._inputAt = this._touchAt = performance.now(); this._dirty = true;
      }
      if (e.pointerType === 'mouse') this._hover = { sx: e.offsetX * dpr(), sy: e.offsetY * dpr(), cx: e.clientX, cy: e.clientY };
    });
    const up = (e) => {
      if (!pts.has(e.pointerId)) return;
      pts.delete(e.pointerId);
      if (pts.size < 2) { pinch0 = null; mid0 = null; twistOn = false; }
      if (pts.size === 0 && moved < (ray() ? VR_TAP_PX : MOUSE_TAP_PX) && e.type === 'pointerup') {
        const r = ray(), sx = r ? d0x : e.offsetX, sy = r ? d0y : e.offsetY;
        this._tap(sx * dpr(), sy * dpr(), { client: { x: e.clientX + sx - e.offsetX, y: e.clientY + sy - e.offsetY }, kind });
      }
    };
    cv.addEventListener('pointerup', up);
    cv.addEventListener('pointercancel', up);
    cv.addEventListener('pointerleave', (e) => {
      if (e.pointerType === 'mouse') { this._hover = null; if (this._tip) this._tip.hidden = true; }
    });
  }

  // a tap at canvas px (sx, sy): a red beam spot = the beam (onSpot, above
  // everything); a player dot = follow / unfollow (combat off); a placed
  // label = what it names; anything else = the point there
  _tap(sx, sy, info = {}) {
    const v = this._full; if (!v) return;
    const cv = this._fullCv, W = cv.width, H = cv.height, d = W / Math.max(1, cv.clientWidth);
    const spot = this.spotsOn && this.onSpot ? spotAt(this.spots, v, W, H, sx, sy, SPOT_HIT_PX * d) : null;
    if (spot) { this._fullAt = 0; this.onSpot(spot, info); return; }
    const c = Math.cos(v.rot || 0), s = Math.sin(v.rot || 0);
    let best = null, bestD = TAP_PX * d;
    for (const p of this.list) {
      const ex = (p.x - v.cx) / v.mpp, ez = (p.z - v.cz) / v.mpp;
      const px = W / 2 + ex * c + ez * s, py = H / 2 - ex * s + ez * c;
      const dd = Math.hypot(px - sx, py - sy);
      if (dd < bestD) { bestD = dd; best = p; }
    }
    this._fullAt = 0;
    if (best) { this.followId = this.followId === best.id ? null : best.id; return; }
    if (!this.onTapWorld) return;
    const mppCss = v.mpp * d;
    const pickR = (PICK_PX[info.kind] || PICK_PX.mouse) * mppCss;
    // a label on the static layer (only while it shows this very view)
    const st = this._static;
    if (st && this._sameView(st.view, v)) {
      for (let i = this._placed.length - 1; i >= 0; i--) {
        const p = this._placed[i], b = p.box;
        if (sx < b[0] || sx > b[2] || sy < b[1] || sy > b[3]) continue;
        const e = p.c.ent;
        this.onTapWorld(e.x, e.z, { ...info, pickR: Math.min(pickR, 30), poi: e.poi || null, name: e.name, kind: e.kind, ent: e, mppCss });
        return;
      }
    }
    const [x, z] = toWorld(v, W, H, sx, sy);
    this.onTapWorld(x, z, { ...info, pickR, mppCss });
  }

  _sameView(a, b) {
    return !!a && Math.abs(a.cx - b.cx) < 1e-6 && Math.abs(a.cz - b.cz) < 1e-6
      && Math.abs(a.mpp - b.mpp) < 1e-9 && Math.abs((a.rot || 0) - (b.rot || 0)) < 1e-9;
  }

  get fullOpen() { return !!this._full; }

  toggleFull(o) { if (this._full) this.closeFull('toggle'); else this.openFull(o); }

  // the full Map may open: in the game (flat / phone) or before entry
  _mapOk() { return this.preEntry || (this.enabled && this.mode !== 'vr'); }

  // headingUp (the phone's tilt): the map follows you until you pan and opens
  // at your heading; on the phone it then spins to north-up (north lock, the
  // default); elsewhere it turns with you. Otherwise north-up: the whole world the first time, then
  // the zoom it was left at, centred on you. at {x, z, mpp?}: centred there
  // instead (not tracking) — before entry, e.g. a GPS fix. onWantOpen(o) →
  // true: main.js runs the inverse beam first and opens with now: true.
  openFull(o = {}) {
    const { headingUp = false, at = null } = o;
    if (!this._mapOk() || !this._fullWrap) return;
    if (!o.now && this.onWantOpen && this.onWantOpen(o)) return;
    this._loadImg();
    if (document.pointerLockElement) document.exitPointerLock();
    this._fullWrap.hidden = false;
    this._sizeCanvas(this._fullCv);
    const fit = this._fitMpp();
    const s = this._self;
    const last = this._lastMpp && this._lastMpp < fit * 0.9 ? this._lastMpp : null;
    this._full = at
      ? { cx: at.x, cz: at.z, mpp: Math.min(at.mpp || last || fit, fit), rot: 0, headingUp: false, track: false }
      : headingUp && s
        ? { cx: s.x, cz: s.z, mpp: Math.min(last || fit, 4), rot: s.heading, headingUp: true, track: true }
        : last && s
          ? { cx: s.x, cz: s.z, mpp: last, rot: 0, headingUp: false, track: true }
          : { cx: this.ex / 2, cz: this.ez / 2, mpp: fit, rot: 0, headingUp: false, track: false };
    // the phone's Map is always opened NORTH-LOCKED: the tilt map starts at the
    // heading you were looking along, then spins to north-up and stays there
    if (this.mode === 'mobile') {
      this._full.headingUp = false;
      this._spinNorth(this._full);
    }
    this._fullAt = 0; this._dirty = true;
    this._refine();
    this._syncNorthBtn();
    this._syncSelfBtns();
    if (this.onOpen) this.onOpen();
  }

  // BEFORE ENTRY: the full Map as the page. entryMode ('flat' | 'mobile' |
  // 'vr', the launch tap) picks the layout: the phone's iff 'mobile', else the
  // desktop's (VR = the Quest browser's flat page, the controller ray as
  // mouse). No game mode is needed; × still reports onClose('x').
  openPreEntry(entryMode, o = {}) {
    this.preEntry = true;
    this._self = null;
    this.followId = null;
    this.mode = entryMode === 'mobile' ? 'mobile' : 'flat';
    this._entry = entryMode;
    this._hintAt = performance.now();
    if (this._tiles) this._tiles.cap = this.mode === 'mobile' ? MOBILE_CAP : CAP;
    if (this._miniWrap) { this._miniWrap.hidden = true; this._miniWrap.classList.toggle('mobile', this.mode === 'mobile'); }
    if (this._xBtn) this._xBtn.title = t('player.map.leave');
    if (!this._full) this.openFull(o);
  }

  // the beam (entry) — or back to the landing page: the Map stops being the
  // page. It closes (onClose 'enter' when it was open); main.js then runs
  // setMode(mode) / update() as in the game.
  endPreEntry() {
    if (!this.preEntry) return;
    this.preEntry = false;
    this.closeFull('enter');
    this.mode = null;
  }

  // the full Map's view now ({cx, cz, mpp, rot} | null): the beam flight's 2D stage starts there
  fullView() { const v = this._full; return v ? { cx: v.cx, cz: v.cz, mpp: v.mpp, rot: v.rot || 0 } : null; }
  // the view it was closed at (the inverse beam's descent starts there)
  lastView() { return this._lastView || null; }

  // 🧭 (heading-up) needs a self, ◎ a self or a GPS fix; ⏻ is the game's
  _syncSelfBtns() {
    const has = !!this._self, me = has || !!this.gpsFix, quit = !this.preEntry;
    if (this._meBtn && this._meBtn.hidden === me) this._meBtn.hidden = !me;
    if (this._northBtn) {
      // the phone's lock is always there (before entry it stays locked: disabled)
      const ph = this.mode === 'mobile', hide = ph ? false : !has;
      if (this._northBtn.hidden !== hide) this._northBtn.hidden = hide;
      if (this._northBtn.disabled !== (ph && !has)) this._northBtn.disabled = ph && !has;
    }
    if (this._quitBtn && this._quitBtn.hidden === quit) this._quitBtn.hidden = !quit;
  }

  // why: 'toggle' (N / the map button), 'rmb', 'x' (the × button), 'tilt',
  // 'look' (a map card's 👁), 'beam' (a map card's ⤳), 'mode', 'off', 'enter' (endPreEntry) — handed
  // to onClose
  closeFull(why = '') {
    const was = !!this._full;
    if (was) this._lastView = this.fullView();
    this._full = null;
    if (this._mc) this._mc.pause();
    this.sel = null;
    this.hl = null;
    this._hover = null;
    if (this._tip) this._tip.hidden = true;
    if (this._fullWrap) this._fullWrap.hidden = true;
    if (was && this.onClose) this.onClose(why);
  }

  _sizeCanvas(cv) {
    const d = Math.min(devicePixelRatio || 1, 2);
    const w = Math.round(cv.clientWidth * d), h = Math.round(cv.clientHeight * d);
    if (cv.width !== w || cv.height !== h) { cv.width = w; cv.height = h; }
  }

  setMode(mode) {
    this.mode = mode;
    if (this._xBtn && !this.preEntry) this._xBtn.title = (CLOSE_TITLE[mode] || CLOSE_TITLE.flat)();
    if (this._tiles) this._tiles.cap = mode === 'mobile' ? MOBILE_CAP : CAP;
    this._syncDom();
  }

  _syncDom() {
    if (!this._miniWrap) return;
    const dom = this.enabled && (this.mode === 'flat' || this.mode === 'mobile');
    this._miniWrap.hidden = !dom || !this._self;
    this._miniWrap.classList.toggle('mobile', this.mode === 'mobile');
    if (!dom && !this.preEntry) this.closeFull('off');
    if (dom) this._loadImg();
  }

  // --------------------------------------------------------------- update
  // o: { enabled, combat, remotes (net.remotes), self:{x,z,heading} | null
  //      (null = not placed: before entry), now, wedge {half, len, range}
  //      (flat / phone), xrLeft (left controller Object3D, VR), eye
  //      (THREE.Camera, VR) }. Before entry (preEntry) the full Map runs with
  //      enabled false and self null.
  update(o) {
    const en = !!o.enabled || this.preEntry;
    this._self = o.self || null;
    if (en !== this.enabled) {
      this.enabled = en;
      if (!this.enabled) this.followId = null;
      this._syncDom();
    }
    if (!this.enabled) { if (this._wrist) this._wrist.mesh.visible = false; return; }
    this.combat = !!o.combat;
    this.wedge = o.wedge || null;
    this.list = [];
    if (this.combat) this.followId = null;
    for (const [id, r] of (this.combat ? [] : o.remotes)) {
      if (r.bot || !r.ent || !r.ent.alive) continue;
      const p = r.group ? r.group.position : null;
      this.list.push({ id, name: r.ent.n || `player-${id}`,
        x: p ? p.x : r.ent.p[0], z: p ? p.z : r.ent.p[2], st: r.ent.st });
    }
    if (this.followId !== null && !this.list.some((p) => p.id === this.followId)) {
      this.followId = null;     // they left (or went down) — nothing to follow
    }
    if (this.mode === 'vr') this._updateWrist(o);
    else if (this._miniWrap) this._updateDom(o.now, o.dt);
  }

  _updateDom(now, dt) {
    const s = this._self;
    // no self (before entry): no mini map, no 🧭 (◎: the GPS fix)
    const mini = !!s && !this.preEntry;
    if (this._miniWrap.hidden === mini) this._miniWrap.hidden = !mini;
    this._syncSelfBtns();
    if (mini) this._miniTurn(s, now, dt);
    if (s && now - this._miniAt > MINI_MS) {
      this._miniAt = now;
      this._sizeCanvas(this._mini);
      const W = this._mini.width;
      const R = this.cfg.mini_radius_m || 700;
      this._paint(this._mini.getContext('2d'), W, W,
        { cx: s.x, cz: s.z, mpp: R / (W / 2), rot: 0, round: true, names: false, streets: true, north: false,
          px: W / Math.max(1, this._mini.clientWidth) });
      const f = this.list.find((p) => p.id === this.followId);
      this._miniNote.hidden = !f;
      if (f) {
        const d = Math.hypot(f.x - s.x, f.z - s.z);
        let brg = Math.atan2(f.x - s.x, -(f.z - s.z)) * 180 / Math.PI;
        if (brg < 0) brg += 360;
        const dir = [t('player.map.dirN'), t('player.map.dirNE'), t('player.map.dirE'), t('player.map.dirSE'), t('player.map.dirS'), t('player.map.dirSW'), t('player.map.dirW'), t('player.map.dirNW')][Math.round(brg / 45) % 8];
        this._miniNote.textContent = `➜ ${f.name} · ${fmtDist(d)} ${dir}`;
        this._miniNote.style.borderColor = playerCss(f.id);
      }
    }
    if (this._full && !this._covered && (this._dirty || now - this._fullAt > FULL_MS)) this._fullFrame(now);
    if (this._full && now - this._roAt > HOVER_MS) {
      this._roAt = now;
      this._updateReadout();
    }
  }

  // THE BEAM-IN SCRIM (main.js showBeamIn / hideBeamIn): while it is up the
  // full Map is not repainted — it shows through the scrim (index.html
  // #beam-in, alpha >= 0.94: 6 %) frozen at the Beam. The scrim going
  // repaints it HERE, in the task that hides the scrim, so its first visible
  // frame is a fresh one (check-mapfirst m)
  setCovered(on) {
    on = !!on;
    if (on === this._covered) return;
    this._covered = on;
    if (!on && this._full) this._fullFrame(performance.now());
  }

  // one full-Map paint (+ its note line)
  _fullFrame(now) {
    const s = this._self;
    this._fullAt = now;
    this._dirty = false;
    this._sizeCanvas(this._fullCv);
    const cv = this._fullCv, v = this._full;
    if (v.headingUp && s) v.rot = s.heading;
    if (v.anim) {                         // the spin to north-up (ease in-out)
      const k = Math.min(1, (now - v.anim.t0) / SPIN_MS);
      v.rot = v.anim.from * (1 - k * k * (3 - 2 * k));
      if (k >= 1) { v.rot = 0; v.anim = null; }
      this._dirty = true;
    }
    this._syncNorthBtn();
    if (v.track && s) { v.cx = s.x; v.cz = s.z; }
    this._paintFull(cv.getContext('2d'), cv.width, cv.height, v, cv.width / Math.max(1, cv.clientWidth), now);
    const f = this.list.find((p) => p.id === this.followId);
    const n = this.list.length;
    // before entry: loading + the device's few words, then nothing
    if (!s && !this._mapReady && this._imgOk && (!this._tiles || this._tiles.inFlight === 0)
      && now - this._hintAt > HINT_MS) this._mapReady = true;
    const loading = !s && !this._mapReady;
    if (this._fullNote.classList.contains('loading') !== loading) this._fullNote.classList.toggle('loading', loading);
    const fl = this._flash && now < this._flash.until ? this._flash.text : null;
    const verb = (NOTE_VERB[this.mode] || NOTE_VERB.flat)();
    this._fullNote.textContent = fl ? fl : !s ? (loading ? t('player.map.loading', { hint: (HINT[this._entry] || HINT.flat)() }) : '')
      : this.combat ? t('player.map.combat')
      : f ? t('player.map.following', { name: f.name, verb })
        : n ? (n > 1 ? t('player.map.othersN', { n, verb: verb.toLowerCase() }) : t('player.map.others1', { verb: verb.toLowerCase() }))
          : t('player.map.details', { verb });
  }

  // the minimap heading-up: up = the direction you MOVE (position deltas —
  // walking, a ride, the bike, a GPS walk all count; a jump > 60 m = a
  // placement, it restarts), the view's heading when slower than MOVE_MIN;
  // eased, applied as a CSS rotation of the north-up canvas (no repaint)
  // with #pmap-n kept upright on the rim at true north
  _miniTurn(s, now, dt) {
    const m = this._mv;
    if (!m || now - m.t > 1000 || Math.hypot(s.x - m.x, s.z - m.z) > 60) {
      this._mv = { x: s.x, z: s.z, t: now, vx: 0, vz: 0 };
    } else if (now - m.t >= 100) {
      const k = 1000 / (now - m.t);
      m.vx += ((s.x - m.x) * k - m.vx) * 0.5; m.vz += ((s.z - m.z) * k - m.vz) * 0.5;
      m.x = s.x; m.z = s.z; m.t = now;
    }
    const v = this._mv;
    const want = Math.hypot(v.vx, v.vz) > MOVE_MIN ? Math.atan2(v.vx, -v.vz) : s.heading;
    const d = Math.atan2(Math.sin(want - this._miniRot), Math.cos(want - this._miniRot));
    const a = this._miniRot + d * Math.min(1, (dt || 0.016) * 6);
    this._miniRot = Math.atan2(Math.sin(a), Math.cos(a));
    if (Math.abs(this._miniRot - this._miniCss) < 0.004) return;
    const r = this._miniRot;
    this._miniCss = r;
    this._mini.style.transform = `rotate(${(-r).toFixed(4)}rad)`;
    if (this._miniN) {
      this._miniN.style.transform = `translate(-50%, -50%) rotate(${(-r).toFixed(4)}rad) `
        + `translateY(calc(-1 * var(--pr))) rotate(${r.toFixed(4)}rad)`;
    }
  }

  // the readout (bottom-left): LV95 E/N (game x/z off the Swiss grid) +
  // elevation of the cursor (desktop) or the centre (phone / no cursor);
  // the desktop tooltip names the nearest thing under the cursor
  _updateReadout() {
    const v = this._full, cv = this._fullCv;
    if (!v || !this._readout) return;
    const W = cv.width, H = cv.height, d = W / Math.max(1, cv.clientWidth);
    const h = this.mode === 'flat' ? this._hover : null;
    const [x, z] = h ? toWorld(v, W, H, h.sx, h.sy) : [v.cx, v.cz];
    const S = this.src;
    const lv = S.lv95 ? S.lv95(x, z) : null;
    const el = S.elevAt ? S.elevAt(x, z) : null;
    const pos = lv ? `E ${fmtInt(lv.E)} · N ${fmtInt(lv.N)}` : `x ${Math.round(x)} · z ${Math.round(z)}`;
    const ele = el ? `${el.approx ? '≈ ' : ''}${fmtInt(el.y)} m` : '— m';
    const txt = `${h ? '' : '⌖ '}${pos} · ${ele}`;
    if (this._readout.textContent !== txt) this._readout.textContent = txt;
    if (!this._tip) return;
    if (!h || x < 0 || z < 0 || x > this.ex || z > this.ez) { this._tip.hidden = true; return; }
    const mppCss = v.mpp * d;
    const reg = layerOn('buildings', mppCss) && S.registry ? registryIndex(S.registry()) : null;
    let pick = hoverPick({ gazetteer: S.gazetteer, reg, zones: this.getZones(),
      guideposts: layerOn('guideposts', mppCss) ? S.guideposts : null }, x, z, 14 * mppCss);
    if (!pick && this.chips.amen && this.extras.amenities) {
      const a = amenityNear(this.extras.amenities, x, z, 14 * mppCss, mppCss);
      if (a) pick = { icon: AMENITY[a.p.k][1], name: a.p.name ? `${amenityLabel(a.p)} · ${a.p.name}` : amenityLabel(a.p) };
    }
    if (!pick) { this._tip.hidden = true; return; }
    const t = `${pick.icon} ${pick.name}`;
    if (this._tip.textContent !== t) this._tip.textContent = t;
    this._tip.style.transform = `translate(${Math.round(h.cx + 14)}px, ${Math.round(h.cy + 16)}px)`;
    this._tip.hidden = false;
  }

  // ------------------------------------------------------------ VR wrist
  // the wrist face as a laser target (vrui.js: a pull opens the Map panel)
  get wristFace() { return this._wrist ? this._wrist.face : null; }

  // the in-headset Map panel (vrui.js): this painter (the mini / wrist one) on
  // a rectangle, view {cx, cz, mpp, rot, names, stations, streets, px}; the
  // imagery loads as for the wrist
  paintPanel(g, W, H, view) {
    this._loadImg();
    this._paint(g, W, H, view);
  }

  _ensureWrist() {
    if (this._wrist) return this._wrist;
    const canvas = document.createElement('canvas');
    canvas.width = canvas.height = WRIST_PX;
    const tex = new THREE.CanvasTexture(canvas);
    tex.colorSpace = THREE.SRGBColorSpace;
    const mesh = new THREE.Mesh(new THREE.PlaneGeometry(WRIST_M, WRIST_M),
      new THREE.MeshBasicMaterial({ map: tex, transparent: true, opacity: 0,
        depthWrite: false, toneMapped: false }));   // in-world UI: outside the display curve
    // "watch face" on the back of the left wrist: behind + above the grip,
    // face along the controller's +Y, canvas-up along -X (away from you when
    // the forearm lies across the body, i.e. the direction you face)
    mesh.position.set(0, 0.035, 0.1);
    mesh.rotation.set(-Math.PI / 2, Math.PI / 2, 0, 'YXZ');
    mesh.renderOrder = 10;
    mesh.visible = false;
    this._wrist = { mesh, canvas, tex, op: 0, face: { mesh, size: WRIST_M } };
    this._loadImg();
    return this._wrist;
  }

  _updateWrist(o) {
    if (!o.self) { if (this._wrist) this._wrist.mesh.visible = false; return; }   // not placed
    const w = this._ensureWrist();
    if (o.xrLeft && w.mesh.parent !== o.xrLeft) o.xrLeft.add(w.mesh);
    if (!w.mesh.parent || !o.eye) return;
    // shown while the face points at the eyes AND the eyes look at it
    w.mesh.getWorldPosition(_v);
    o.eye.getWorldPosition(_f);
    const toEye = _f.sub(_v); const dist = toEye.length(); toEye.divideScalar(dist || 1);
    _n.set(0, 0, 1).transformDirection(w.mesh.matrixWorld);
    const facing = _n.dot(toEye);
    o.eye.getWorldDirection(_v);
    const looking = -_v.dot(toEye);
    const want = dist < 0.75 && facing > 0.5 && looking > 0.75 ? 1 : 0;
    w.op += (want - w.op) * Math.min(1, (o.dt || 0.016) * 10);
    w.mesh.material.opacity = w.op;
    w.mesh.visible = w.op > 0.02;
    if (!w.mesh.visible || o.now - this._wristAt < WRIST_MS) return;
    this._wristAt = o.now;
    const R = this.cfg.wrist_radius_m || 900;
    this._paint(w.canvas.getContext('2d'), WRIST_PX, WRIST_PX,
      { cx: o.self.x, cz: o.self.z, mpp: R / (WRIST_PX / 2), rot: o.self.heading,
        round: true, names: true, px: 2.4 });
    if (this.slopeDeg !== null) {
      // the slope ahead, low on the face (canvas-up = the way you face)
      const g = w.canvas.getContext('2d');
      g.textAlign = 'center'; g.textBaseline = 'middle';
      this._label(g, slopeLabel(this.slopeDeg), WRIST_PX / 2, WRIST_PX - 40 * 2.4, '#fff', 15, 2.4);
    }
    w.tex.needsUpdate = true;
  }

  // the trail layer, inside the world transform (metres): k = metres per
  // CSS px; trails whose bbox misses the view circle (cx, cz, R) are skipped;
  // light: on the street map (ungraded paths dark grey)
  _paintTrails(g, cx, cz, R, k, light = false) {
    const x0 = cx - R, x1 = cx + R, z0 = cz - R, z1 = cz + R;
    for (const [m, c, w, dash] of TRAIL_STYLE) {
      const col = light && m === 'none' ? STREET_NONE : c;
      const list = this.trails[m];
      if (!list || !list.length) continue;
      g.beginPath();
      let any = false;
      for (const t of list) {
        const b = t.bbox;
        if (b[2] < x0 || b[0] > x1 || b[3] < z0 || b[1] > z1) continue;
        const P = t.pts;
        g.moveTo(P[0], P[1]);
        for (let i = 2; i < P.length; i += 2) g.lineTo(P[i], P[i + 1]);
        any = true;
      }
      if (!any) continue;
      g.strokeStyle = col; g.lineWidth = w * k;
      g.lineCap = dash && dash[0] < 1 ? 'round' : 'butt';
      g.setLineDash(dash ? dash.map((d) => d * k) : []);
      g.stroke();
    }
  }

  // the full Map's trail ladder (world transform, k = m per CSS px): route
  // bands ≤ 16 · MTB colours ≤ 4 (MTB chip) · official network ≤ 16 / every
  // path ≤ 8 / fine styling ≤ 1 (always) · incline chevrons ≤ 1 · TLM
  // structures ≤ 0.5. Every stroke bbox-culled, one per style key.
  _paintTrailLadder(g, rect, k) {
    const bk = this._bk, ch = this.chips, X = this.extras;
    if (layerOn('routes', k) && X.routes) paintRouteBands(g, X.routes, rect, k, ch, this.hl ? this.hl.ri : -1);
    if (!bk) return;
    if (ch.mtb && layerOn('mtb', k)) paintMtb(g, bk.mtb, rect, k);
    const b = layerOn('trailstyle', k) ? bk.fine : layerOn('trails', k) ? bk.coarse : layerOn('network', k) ? bk.network : null;
    if (b) paintTrailBuckets(g, b, rect, k);
    if (layerOn('chevrons', k)) paintChevrons(g, bk.inc, rect, k);
    if (layerOn('structs', k)) paintStructs(g, X.structs, rect, k);
  }

  // lift lines (funicular + aerial), world transform
  _paintLifts(g, k) {
    g.setLineDash([]); g.lineCap = 'butt';
    g.strokeStyle = 'rgba(255,201,60,.9)'; g.lineWidth = 2 * k;
    for (const l of this.rides.lines) {
      const pts = l.path && l.path.points;
      if (!pts || pts.length < 2) continue;
      g.beginPath(); g.moveTo(pts[0][0], pts[0][2]);
      for (let i = 1; i < pts.length; i++) g.lineTo(pts[i][0], pts[i][2]);
      g.stroke();
    }
  }

  // ---------------------------------------------- shared overlay pieces
  _label(g, text, x, y, color, size, px) {
    g.font = `600 ${size * px}px system-ui, sans-serif`;
    g.lineWidth = 3 * px; g.strokeStyle = 'rgba(0,0,0,.8)'; g.fillStyle = color;
    g.strokeText(text, x, y); g.fillText(text, x, y);
  }

  // the inspect pin: a gold drop marker (+ its label when names)
  _drawPin(g, toS, px, names) {
    if (!this.mark) return;
    const [sx, sy] = toS(this.mark.x, this.mark.z);
    const k = 7 * px;
    g.beginPath(); g.moveTo(sx, sy);
    g.arc(sx, sy - 1.9 * k, k, Math.PI * 0.75, Math.PI * 2.25);
    g.closePath();
    g.fillStyle = '#ffc93c'; g.fill(); g.lineWidth = 1.5 * px; g.strokeStyle = '#111'; g.stroke();
    g.beginPath(); g.arc(sx, sy - 1.9 * k, k * 0.38, 0, Math.PI * 2); g.fillStyle = '#111'; g.fill();
    if (names && this.mark.label) {
      g.textAlign = 'left'; g.textBaseline = 'middle';
      this._label(g, this.mark.label, sx + k + 4 * px, sy - 1.9 * k, '#ffe7a8', 12, px);
    }
  }

  // the beam spots: a red disc, white rim, a dark halo (on imagery and on
  // the street map alike) + the name; the same CSS size at every zoom
  _drawSpots(g, toS, px, names) {
    const r = SPOT_PX * px;
    g.textAlign = 'left'; g.textBaseline = 'middle';
    for (const p of this.spots) {
      const [sx, sy] = toS(p.x, p.z);
      g.beginPath(); g.arc(sx, sy, r, 0, Math.PI * 2);
      g.fillStyle = '#ef4444'; g.fill();
      g.lineWidth = 5 * px; g.strokeStyle = 'rgba(8,10,16,.55)'; g.stroke();
      g.lineWidth = 3 * px; g.strokeStyle = '#fff'; g.stroke();
      g.beginPath(); g.arc(sx, sy, r * 0.32, 0, Math.PI * 2); g.fillStyle = '#fff'; g.fill();
      if (names) this._label(g, p.label, sx + r + 5 * px, sy, '#fff', 13, px);
    }
  }

  // other players: dot (+ name); off a round map → a rim arrow
  _drawPlayers(g, toS, W, H, view) {
    const px = view.px, rim = W / 2 - 9 * px, s = this._self;
    g.textAlign = 'left'; g.textBaseline = 'middle';
    for (const p of this.list) {
      let [sx, sy] = toS(p.x, p.z);
      const col = playerCss(p.id);
      const fol = p.id === this.followId;
      const rx = sx - W / 2, ry = sy - H / 2, rr = Math.hypot(rx, ry);
      if (view.round && rr > rim) {
        const a = Math.atan2(ry, rx);
        sx = W / 2 + Math.cos(a) * rim; sy = H / 2 + Math.sin(a) * rim;
        g.save(); g.translate(sx, sy); g.rotate(a);
        const k = (fol ? 8 : 6) * px;
        g.beginPath(); g.moveTo(k, 0); g.lineTo(-k * 0.7, -k * 0.75); g.lineTo(-k * 0.7, k * 0.75); g.closePath();
        g.fillStyle = col; g.fill(); g.lineWidth = 1.5 * px; g.strokeStyle = '#fff'; g.stroke();
        g.restore();
        if (view.names) {
          const tx = sx - Math.cos(a) * 14 * px;
          g.textAlign = Math.cos(a) > 0 ? 'right' : 'left';
          this._label(g, s ? `${p.name} ${fmtDist(Math.hypot(p.x - s.x, p.z - s.z))}` : p.name, tx, sy - Math.sin(a) * 14 * px, col, 12, px);
          g.textAlign = 'left';
        }
        continue;
      }
      const r = (fol ? 7 : 5) * px;
      if (fol) { g.beginPath(); g.arc(sx, sy, r + 4 * px, 0, Math.PI * 2); g.strokeStyle = col; g.lineWidth = 2 * px; g.stroke(); }
      g.beginPath(); g.arc(sx, sy, r, 0, Math.PI * 2);
      g.fillStyle = col; g.fill(); g.lineWidth = 1.5 * px; g.strokeStyle = '#fff'; g.stroke();
      if (view.names) this._label(g, p.name, sx + r + 5 * px, sy, '#fff', 13, px);
    }
  }

  // you: an arrow along your heading
  _drawSelf(g, toS, rot, px) {
    const s = this._self;
    if (!s) return;                       // not placed (before entry)
    const [sx, sy] = toS(s.x, s.z);
    g.save(); g.translate(sx, sy); g.rotate(s.heading - rot);
    const k = 8 * px;
    g.beginPath(); g.moveTo(0, -k * 1.3); g.lineTo(k * 0.85, k); g.lineTo(0, k * 0.45); g.lineTo(-k * 0.85, k); g.closePath();
    g.fillStyle = '#fff'; g.fill(); g.lineWidth = 1.5 * px; g.strokeStyle = '#111'; g.stroke();
    g.restore();
  }

  // north: a tick on the rim (round maps rotate on the wrist); a rotated
  // full map gets an N arrow in its corner
  _drawNorth(g, W, H, view) {
    const { rot, px } = view;
    if (!view.round && rot) {
      const nx = W - 30 * px, ny = 92 * px, k = 11 * px;
      g.save(); g.translate(nx, ny); g.rotate(-rot);
      g.beginPath(); g.moveTo(0, -k); g.lineTo(k * 0.55, k * 0.7); g.lineTo(0, k * 0.3); g.lineTo(-k * 0.55, k * 0.7); g.closePath();
      g.fillStyle = '#ff7d6e'; g.fill(); g.lineWidth = 1.5 * px; g.strokeStyle = '#111'; g.stroke();
      g.restore();
      g.textAlign = 'center';
      this._label(g, 'N', nx, ny + k + 9 * px, '#fff', 11, px);
    }
    if (view.round) {
      const nx = W / 2 - Math.sin(rot) * (W / 2 - 13 * px), ny = H / 2 - Math.cos(rot) * (W / 2 - 13 * px);
      g.textAlign = 'center';
      this._label(g, 'N', nx, ny, '#fff', 11, px);
    }
  }

  // ------------------------------------------------- painter: mini + wrist
  // view: {cx, cz, mpp (m per canvas px), rot (map-up bearing, rad), round,
  //        names, stations, px (canvas px per CSS px — line/text scale)}
  _paint(g, W, H, view) {
    const { cx, cz, mpp, rot, px } = view;
    const cos = Math.cos(rot), sin = Math.sin(rot);
    const toS = (x, z) => {
      const dx = (x - cx) / mpp, dz = (z - cz) / mpp;
      return [W / 2 + dx * cos + dz * sin, H / 2 - dx * sin + dz * cos];
    };
    g.setTransform(1, 0, 0, 1, 0, 0);
    g.clearRect(0, 0, W, H);
    g.save();
    if (view.round) { g.beginPath(); g.arc(W / 2, H / 2, W / 2 - px, 0, Math.PI * 2); g.clip(); }
    g.fillStyle = view.streets ? STREET_BG : '#18202c';
    g.fillRect(0, 0, W, H);

    // world layer: backdrop (imagery, or the streets) + lift lines, in metres
    g.save();
    applyWorld(g, view, W, H);
    if (view.streets) {
      const S = this.src, rect = viewRect(view, W, H), k = mpp * px;
      paintLakes(g, S.lakes, rect, k);
      paintRivers(g, S.rivers, rect, k);
      paintRoads(g, S.graph, rect, k);
    } else if (this._imgOk) g.drawImage(this._img, 0, 0, this._imgM[0], this._imgM[1]);
    else {
      g.strokeStyle = 'rgba(255,255,255,.12)'; g.lineWidth = mpp * px;
      for (let x = 0; x <= this.ex; x += 1000) { g.beginPath(); g.moveTo(x, 0); g.lineTo(x, this.ez); g.stroke(); }
      for (let z = 0; z <= this.ez; z += 1000) { g.beginPath(); g.moveTo(0, z); g.lineTo(this.ex, z); g.stroke(); }
    }
    if (this.trails) this._paintTrails(g, cx, cz, Math.hypot(W, H) / 2 * mpp, mpp * px, !!view.streets);
    this._paintLifts(g, mpp * px);
    g.restore();
    if (view.round && !view.streets) { g.fillStyle = 'rgba(10,14,22,.18)'; g.fillRect(0, 0, W, H); }

    // stations: gold rings + names
    const zones = view.stations && this.getZones();
    if (zones && zones.zones) {
      g.textAlign = 'left'; g.textBaseline = 'middle';
      for (const zn of zones.zones) {
        const [sx, sy] = toS(zn.cx, zn.cz);
        g.beginPath(); g.arc(sx, sy, 3.5 * px, 0, Math.PI * 2);
        g.fillStyle = '#ffc93c'; g.fill();
        if (!zn.lite) this._label(g, zn.name, sx + 6 * px, sy, '#ffe7a8', 11, px);
      }
    }
    this._drawPin(g, toS, px, view.names);
    this._drawPlayers(g, toS, W, H, view);
    if (view.spots && this.spots.length) this._drawSpots(g, toS, px, view.names);
    this._drawSelf(g, toS, rot, px);
    if (view.north !== false) this._drawNorth(g, W, H, view);
    g.restore();
    if (view.round) {
      g.beginPath(); g.arc(W / 2, H / 2, W / 2 - px, 0, Math.PI * 2);
      g.lineWidth = 2 * px; g.strokeStyle = 'rgba(255,255,255,.55)'; g.stroke();
    }
  }

  // ------------------------------------------------- painter: the full Map
  // the resident-data signature of what the static layer shows at k CSS m/px
  _dataSig(k) {
    const S = this.src, gz = S.gazetteer, zs = this.getZones();
    let sig = `${gz && gz.pois ? gz.pois.length : 0}|${(S.lakes || []).length}|${(S.rivers || []).length}`
      + `|${zs && zs.zones ? zs.zones.length : 0}|${this.rides.lines.length}|${this._trailGen}|x${this._extraGen}`;
    if (layerOn('roads', k)) { const gr = S.graph; sig += `|r${gr && gr.ways ? gr.ways.length : 0}`; }
    if (layerOn('buildings', k)) {
      const reg = S.registry ? S.registry() : null;
      sig += `|b${reg ? 1 : 0}|f${S.collider && S.collider.tiles ? [...S.collider.tiles.keys()].join(',') : ''}`;
    }
    if (layerOn('objects', k)) { sig += `|o${S.objects && S.objects() ? 1 : 0}|g${S.guideposts ? 1 : 0}`; }
    if (layerOn('trees', k)) {
      // a landed tree / shrub tile bumps _dataGen (the arrays are world-wide, preallocated)
      sig += `|t${S.forest ? S.forest._dataGen : 0}|s${S.shrubs ? S.shrubs._dataGen || 0 : 0}`;
    }
    return sig;
  }

  _measure(g) {
    return (text, size) => {
      const key = size + '|' + text;
      let w = this._wcache.get(key);
      if (w === undefined) {
        g.font = `700 ${size}px system-ui, -apple-system, 'Segoe UI', sans-serif`;
        w = g.measureText(text).width;
        if (this._wcache.size > 6000) this._wcache.clear();
        this._wcache.set(key, w);
      }
      return w;
    };
  }

  // the static layer: imagery + every zoom-sensitive layer + the labels;
  // keepLabels: only the imagery changed (a class tile, the underlay) — the
  // last label placement is drawn again, not recomputed
  _renderStatic(g, W, H, v, px, keepLabels = false) {
    const k = v.mpp * px;                 // metres per CSS px (= the zoom tables' m/px)
    const rect = viewRect(v, W, H);
    const S = this.src;
    g.setTransform(1, 0, 0, 1, 0, 0);
    g.fillStyle = '#18202c';
    g.fillRect(0, 0, W, H);
    g.save();
    applyWorld(g, v, W, H);
    if (this._imgOk) g.drawImage(this._img, 0, 0, this._imgM[0], this._imgM[1]);
    else {
      g.strokeStyle = 'rgba(255,255,255,.12)'; g.lineWidth = k;
      for (let x = 0; x <= this.ex; x += 1000) { g.beginPath(); g.moveTo(x, 0); g.lineTo(x, this.ez); g.stroke(); }
      for (let z = 0; z <= this.ez; z += 1000) { g.beginPath(); g.moveTo(0, z); g.lineTo(this.ex, z); g.stroke(); }
    }
    // the zoomed Map: the 512 m class tiles once finer than the device's
    // underlay texel (the 16 m first paint alone does not pull tiles the
    // refine covers; the padding past the extent clipped)
    if (this._tiles) {
      if (tilesOn(v.mpp, this._underM())) {
        const grid = this._mc.grid, list = tilesWanted(v, W, H, this.ex, this.ez, grid);
        this._tiles.want(list);
        g.save(); g.beginPath(); g.rect(0, 0, this.ex, this.ez); g.clip();
        drawTiles(g, this._tiles, list, grid.tile_m);
        g.restore();
      } else this._tiles.want([]);
    }
    paintLakes(g, S.lakes, rect, k);
    paintRivers(g, S.rivers, rect, k);
    if (layerOn('trees', k)) { paintShrubs(g, S.shrubs, rect, k); paintTrees(g, S.forest, rect, k); }
    let reg = null;
    if (layerOn('buildings', k)) {
      reg = registryIndex(S.registry ? S.registry() : null);
      const fp = layerOn('footprints', k) ? paintFootprints(g, S.collider, rect, k) : null;
      paintBuildingDots(g, reg, rect, k, fp);
    }
    if (layerOn('objects', k)) paintObjects(g, S.objects ? S.objects() : null, rect, k);
    if (layerOn('roads', k)) paintRoads(g, S.graph, rect, k);
    this._paintTrailLadder(g, rect, k);
    g.setLineDash([]);
    this._paintLifts(g, k);
    if (layerOn('guideposts', k)) paintGuideposts(g, S.guideposts, rect, k);
    g.restore();

    // screen space: station dots, then the labels
    const zones = this.getZones();
    const toS = (x, z) => {
      const c = Math.cos(v.rot || 0), s = Math.sin(v.rot || 0);
      const dx = (x - v.cx) / v.mpp, dz = (z - v.cz) / v.mpp;
      return [W / 2 + dx * c + dz * s, H / 2 - dx * s + dz * c];
    };
    if (zones && zones.zones) {
      for (const zn of zones.zones) {
        const [sx, sy] = toS(zn.cx, zn.cz);
        if (sx < -10 || sy < -10 || sx > W + 10 || sy > H + 10) continue;
        g.beginPath(); g.arc(sx, sy, 3.5 * px, 0, Math.PI * 2);
        g.fillStyle = '#ffc93c'; g.fill(); g.lineWidth = 1.2 * px; g.strokeStyle = '#111'; g.stroke();
      }
    }
    if (v.bare) this._placed = [];             // the beam's Map wait: its own names (beamlabels.js)
    else if (!keepLabels) {
      const X = this.extras;
      const cands = collectLabels({ view: v, W, H, px, rect, mppCss: k,
        src: { gazetteer: S.gazetteer, zones, graph: S.graph, reg,
          guideposts: layerOn('guideposts', k) ? S.guideposts : null,
          trailLines: this._lines.length ? this._lines : null, routes: X.routes, amenities: X.amenities,
          chips: this.chips, hl: this.hl ? this.hl.ri : -1 } });
      this._placed = placeLabels(cands, W, H, { cell: 6 * px, measure: this._measure(g), pad: 2 * px });
    }
    drawLabels(g, this._placed, px);
  }

  _paintFull(g, W, H, v, px, now) {
    this._paintBase(g, W, H, v, px, now);
    this._paintOverlay(g, W, H, v, px, now);
  }

  // THE BEAM FLIGHT's 2D stage (beamflight.js FlightCover): the full Map's
  // static layer for view {cx, cz, mpp, rot, anim} on another canvas — the
  // same cache, tiles and stale-layer mapping, no overlay (no chrome, pins,
  // dots: the Map under the cover is no longer the page)
  paintFlight(g, W, H, v, now) {
    this._loadImg();
    this._paintBase(g, W, H, v, W / Math.max(1, g.canvas.clientWidth), now);
  }

  _paintBase(g, W, H, v, px, now) {
    const k = v.mpp * px;
    // lkey: everything the labels depend on; key adds the imagery
    const lkey = `${v.cx.toFixed(3)}|${v.cz.toFixed(3)}|${v.mpp.toFixed(6)}|${(v.rot || 0).toFixed(5)}|${W}x${H}|${px}`
      + `|${this.chips.mtb ? 1 : 0}${this.chips.amen ? 1 : 0}|h${this.hl ? this.hl.ri : -1}|b${v.bare ? 1 : 0}`
      + `|${this._dataSig(k)}`;
    const key = `${lkey}|${this._tileGen}|${this._imgGen}`;
    let st = this._static;
    if (!st || st.cv.width !== W || st.cv.height !== H) {
      const cv = document.createElement('canvas');
      cv.width = W; cv.height = H;
      st = this._static = { cv, key: null, lkey: null, view: null, at: -1e9 };
    }
    // "hot" = the view is moving (input, following you, turning with you):
    // re-render at a capped rate, the stale layer mapped in between
    const hot = now - this._inputAt < INPUT_HOT_MS || v.track || v.headingUp || !!v.anim;
    const minMs = this.mode === 'mobile' ? STATIC_MIN_MS_MOBILE : STATIC_MIN_MS;
    // only the imagery changed (tiles landing in view): coalesced to one
    // re-render per TILE_MIN_MS, the label placement kept; meanwhile the
    // last layer (the same view) is drawn as it is
    const imgOnly = st.lkey === lkey;
    if (st.key !== key && (!hot || now - st.at >= minMs || !st.view) && !(imgOnly && now - st.at < TILE_MIN_MS)) {
      this._renderStatic(st.cv.getContext('2d'), W, H, v, px, imgOnly);
      st.key = key; st.lkey = lkey; st.view = { cx: v.cx, cz: v.cz, mpp: v.mpp, rot: v.rot || 0 }; st.at = now;
    }
    g.setTransform(1, 0, 0, 1, 0, 0);
    if (st.key === key) g.drawImage(st.cv, 0, 0);
    else {
      // the last static layer mapped into this view (s1 = T1 ∘ T0⁻¹ s0) over
      // the underlay: smooth pan / zoom between static re-renders
      g.fillStyle = '#18202c'; g.fillRect(0, 0, W, H);
      g.save(); applyWorld(g, v, W, H);
      if (this._imgOk) g.drawImage(this._img, 0, 0, this._imgM[0], this._imgM[1]);
      g.restore();
      const o = st.view;
      g.save();
      g.translate(W / 2, H / 2); g.rotate(-(v.rot || 0)); g.scale(1 / v.mpp, 1 / v.mpp);
      g.translate(o.cx - v.cx, o.cz - v.cz); g.scale(o.mpp, o.mpp); g.rotate(o.rot); g.translate(-W / 2, -H / 2);
      g.drawImage(st.cv, 0, 0);
      g.restore();
    }
  }

  // the dynamic overlay (50 ms): selection, waypoint, wedge, pin, players,
  // the red beam spots, you, north arrow, scale bar, the phone's panning
  // crosshair
  _paintOverlay(g, W, H, v, px, now) {
    const c = Math.cos(v.rot || 0), sn = Math.sin(v.rot || 0);
    const toS = (x, z) => {
      const dx = (x - v.cx) / v.mpp, dz = (z - v.cz) / v.mpp;
      return [W / 2 + dx * c + dz * sn, H / 2 - dx * sn + dz * c];
    };
    const view = { ...v, round: false, names: true, px };
    const s = this._self;
    // your view on the map: the render camera's horizontal fov, longer when
    // zoomed, the centre-range point marked
    const wd = this.wedge;
    if (wd && s) {
      const len = Math.max(wd.len, 70 * px * v.mpp);
      const [ax, ay] = toS(s.x, s.z);
      g.beginPath(); g.moveTo(ax, ay);
      for (let i = 0; i <= 16; i++) {
        const b = s.heading - wd.half + (2 * wd.half * i) / 16;
        const [qx, qy] = toS(s.x + Math.sin(b) * len, s.z - Math.cos(b) * len);
        g.lineTo(qx, qy);
      }
      g.closePath();
      const gr = g.createRadialGradient(ax, ay, 0, ax, ay, len / v.mpp);
      gr.addColorStop(0, 'rgba(103,232,249,0.34)'); gr.addColorStop(1, 'rgba(103,232,249,0.02)');
      g.fillStyle = gr; g.fill();
      g.lineWidth = 1 * px; g.strokeStyle = 'rgba(103,232,249,0.55)'; g.stroke();
      if (wd.range) {
        const [rx, ry] = toS(wd.range.x, wd.range.z);
        g.beginPath(); g.arc(rx, ry, 4 * px, 0, Math.PI * 2);
        g.lineWidth = 1.6 * px; g.strokeStyle = '#67e8f9'; g.stroke();
        g.beginPath(); g.moveTo(rx - 7 * px, ry); g.lineTo(rx + 7 * px, ry); g.moveTo(rx, ry - 7 * px); g.lineTo(rx, ry + 7 * px);
        g.lineWidth = 1 * px; g.stroke();
      }
    }
    // the open map card's target
    if (this.sel) {
      const [sx, sy] = toS(this.sel.x, this.sel.z);
      g.beginPath(); g.arc(sx, sy, 11 * px, 0, Math.PI * 2);
      g.lineWidth = 3 * px; g.strokeStyle = 'rgba(8,10,16,.8)'; g.stroke();
      g.lineWidth = 1.8 * px; g.strokeStyle = '#67e8f9'; g.stroke();
    }
    // the route card's route: a bright line over the dimmed bands (the open
    // stage brightest), in the world transform
    const hr = this.hl && this.extras.routes ? this.extras.routes[this.hl.ri] : null;
    if (hr) {
      const rect = viewRect(v, W, H), k = v.mpp * px;
      const fam = routeFamily(hr), plate = ROUTE_STYLE[fam].plate[0];
      g.save(); applyWorld(g, v, W, H);
      g.lineCap = 'round'; g.lineJoin = 'round'; g.setLineDash([]);
      hr.stages.forEach((st, i) => {
        const b = st.bbox;
        if (b[2] < rect[0] || b[0] > rect[2] || b[3] < rect[1] || b[1] > rect[3]) return;
        const on = this.hl.si < 0 || this.hl.si === i;
        g.beginPath();
        const P = st.pts;
        g.moveTo(P[0], P[1]);
        for (let j = 2; j < P.length; j += 2) g.lineTo(P[j], P[j + 1]);
        g.globalAlpha = on ? 1 : 0.45;
        g.strokeStyle = 'rgba(8,10,16,.75)'; g.lineWidth = (on ? 7 : 5) * k; g.stroke();
        g.strokeStyle = plate; g.lineWidth = (on ? 4 : 2.6) * k; g.stroke();
      });
      g.globalAlpha = 1; g.lineCap = 'butt';
      g.restore();
    }
    // the beacon waypoint (combat off): a gold diamond + its label
    const wp = !this.combat && this.src.waypoint ? this.src.waypoint : null;
    if (wp) {
      const [sx, sy] = toS(wp.x, wp.z), q = 7 * px;
      g.beginPath(); g.moveTo(sx, sy - q); g.lineTo(sx + q, sy); g.lineTo(sx, sy + q); g.lineTo(sx - q, sy); g.closePath();
      g.fillStyle = 'hsl(44 85% 62%)'; g.fill(); g.lineWidth = 1.5 * px; g.strokeStyle = '#111'; g.stroke();
      g.textAlign = 'left'; g.textBaseline = 'middle';
      this._label(g, `📍 ${wp.label}`, sx + q + 4 * px, sy, '#fde68a', 12, px);
    }
    // the phone's GPS fix: an accuracy ring + a blue dot (a tap there = a
    // card at the fix, its ⤳ Beam enters there)
    const gf = this.gpsFix;
    if (gf) {
      const [gx, gy] = toS(gf.x, gf.z), ra = Math.min((gf.acc || 0) / v.mpp, 2 * Math.hypot(W, H));
      if (ra > 8 * px) {
        g.beginPath(); g.arc(gx, gy, ra, 0, Math.PI * 2);
        g.fillStyle = 'rgba(66,133,244,.16)'; g.fill();
        g.lineWidth = 1.2 * px; g.strokeStyle = 'rgba(66,133,244,.65)'; g.stroke();
      }
      g.beginPath(); g.arc(gx, gy, 6.5 * px, 0, Math.PI * 2);
      g.fillStyle = '#4285f4'; g.fill(); g.lineWidth = 2.2 * px; g.strokeStyle = '#fff'; g.stroke();
    }
    this._drawPin(g, toS, px, true);
    this._drawPlayers(g, toS, W, H, view);
    if (this.spotsOn) this._drawSpots(g, toS, px, true);
    if (s) this._drawSelf(g, toS, v.rot || 0, px);
    this._drawNorth(g, W, H, view);
    // scale bar (1-2-5), bottom-left above the readout
    const sb = niceScale(v.mpp * px, 120);
    if (sb) {
      const x0 = 14 * px, y0 = H - 40 * px, L = sb.px * px;
      g.lineWidth = 4 * px; g.strokeStyle = 'rgba(8,10,16,.75)'; g.lineCap = 'square';
      g.beginPath(); g.moveTo(x0, y0 - 5 * px); g.lineTo(x0, y0); g.lineTo(x0 + L, y0); g.lineTo(x0 + L, y0 - 5 * px); g.stroke();
      g.lineWidth = 1.6 * px; g.strokeStyle = '#fff';
      g.beginPath(); g.moveTo(x0, y0 - 5 * px); g.lineTo(x0, y0); g.lineTo(x0 + L, y0); g.lineTo(x0 + L, y0 - 5 * px); g.stroke();
      g.lineCap = 'butt';
      g.textAlign = 'left'; g.textBaseline = 'bottom';
      this._label(g, sb.text, x0 + 4 * px, y0 - 6 * px, '#fff', 11, px);
      g.textBaseline = 'middle';
    }
    // the phone: a centre crosshair while panning (the readout is the centre)
    if (this.mode === 'mobile' && now - this._touchAt < 900) {
      const cx = W / 2, cy = H / 2, a = 9 * px, gap = 3 * px;
      g.lineCap = 'round';
      for (const [lw, col] of [[3.2 * px, 'rgba(8,10,16,.8)'], [1.5 * px, '#fff']]) {
        g.beginPath();
        g.moveTo(cx - a, cy); g.lineTo(cx - gap, cy); g.moveTo(cx + gap, cy); g.lineTo(cx + a, cy);
        g.moveTo(cx, cy - a); g.lineTo(cx, cy - gap); g.moveTo(cx, cy + gap); g.lineTo(cx, cy + a);
        g.lineWidth = lw; g.strokeStyle = col; g.stroke();
      }
      g.lineCap = 'butt';
    }
  }
}
